import { API, client, unwrap } from "@/lib/api-base";
import { bust, cachedGet } from "@/lib/lookup-cache";
export { API };


/* Vendors */
export const listVendors = () =>
  cachedGet("/vendors", {}, () => unwrap(client.get("/vendors")));
export const createVendor = (payload) => {
  bust("/vendors");
  return unwrap(client.post("/vendors", payload));
};
export const updateVendor = (id, payload) => {
  bust("/vendors");
  return unwrap(client.patch(`/vendors/${id}`, payload));
};
export const deleteVendor = (id) => {
  bust("/vendors");
  return unwrap(client.delete(`/vendors/${id}`));
};
export const reactivateVendor = (id) => {
  bust("/vendors");
  return unwrap(client.post(`/vendors/${id}/reactivate`));
};

/* Categories */
export const listCategories = () =>
  cachedGet("/categories", {}, () => unwrap(client.get("/categories")));
export const createCategory = (payload) => {
  bust("/categories");
  return unwrap(client.post("/categories", payload));
};
export const updateCategory = (id, payload) => {
  bust("/categories");
  return unwrap(client.patch(`/categories/${id}`, payload));
};
export const deleteCategory = (id) => {
  bust("/categories");
  return unwrap(client.delete(`/categories/${id}`));
};
export const reactivateCategory = (id) => {
  bust("/categories");
  return unwrap(client.post(`/categories/${id}/reactivate`));
};

/* Payment Methods (anagrafica utente-editabile) */
export const listPaymentMethods = (opts = {}) => {
  const params = opts.includeArchived ? "?include_archived=true" : "";
  return cachedGet(`/customizations/payment-methods${params}`, {}, () =>
    unwrap(client.get(`/customizations/payment-methods${params}`)),
  );
};
export const createPaymentMethod = (payload) => {
  bust("/customizations/payment-methods");
  return unwrap(client.post("/customizations/payment-methods", payload));
};
export const updatePaymentMethod = (id, payload) => {
  bust("/customizations/payment-methods");
  return unwrap(client.put(`/customizations/payment-methods/${id}`, payload));
};
export const deletePaymentMethod = (id) => {
  bust("/customizations/payment-methods");
  return unwrap(client.delete(`/customizations/payment-methods/${id}`));
};
export const reactivatePaymentMethod = (id) => {
  bust("/customizations/payment-methods");
  return unwrap(client.post(`/customizations/payment-methods/${id}/reactivate`));
};

/* Bank Accounts / Casse */
export const listBankAccounts = (opts = {}) => {
  const params = opts.includeArchived ? "?include_archived=true" : "";
  return cachedGet(`/customizations/bank-accounts${params}`, {}, () =>
    unwrap(client.get(`/customizations/bank-accounts${params}`)),
  );
};
export const createBankAccount = (payload) => {
  bust("/customizations/bank-accounts");
  return unwrap(client.post("/customizations/bank-accounts", payload));
};
export const updateBankAccount = (id, payload) => {
  bust("/customizations/bank-accounts");
  return unwrap(client.put(`/customizations/bank-accounts/${id}`, payload));
};
export const deleteBankAccount = (id) => {
  bust("/customizations/bank-accounts");
  return unwrap(client.delete(`/customizations/bank-accounts/${id}`));
};
export const reactivateBankAccount = (id) => {
  bust("/customizations/bank-accounts");
  return unwrap(client.post(`/customizations/bank-accounts/${id}/reactivate`));
};

/* Tags (etichette colorate) */
export const listTags = (opts = {}) => {
  const params = opts.includeArchived ? "?include_archived=true" : "";
  return cachedGet(`/customizations/tags${params}`, {}, () =>
    unwrap(client.get(`/customizations/tags${params}`)),
  );
};
export const createTag = (payload) => {
  bust("/customizations/tags");
  return unwrap(client.post("/customizations/tags", payload));
};
export const updateTag = (id, payload) => {
  bust("/customizations/tags");
  return unwrap(client.put(`/customizations/tags/${id}`, payload));
};
export const deleteTag = (id) => {
  bust("/customizations/tags");
  return unwrap(client.delete(`/customizations/tags/${id}`));
};
export const reactivateTag = (id) => {
  bust("/customizations/tags");
  return unwrap(client.post(`/customizations/tags/${id}/reactivate`));
};

/* Tag Assignments — associazione tag ↔ movimenti */
export const listTagAssignments = (entityType, entityId) =>
  unwrap(
    client.get(
      `/customizations/tags/assignments?entity_type=${entityType}&entity_id=${entityId}`,
    ),
  );

export const listTagAssignmentsBatch = (entityType, entityIds) => {
  if (!entityIds?.length) return Promise.resolve({});
  const ids = entityIds.join(",");
  return unwrap(
    client.get(
      `/customizations/tags/assignments-batch?entity_type=${entityType}&entity_ids=${ids}`,
    ),
  );
};

export const applyTag = (tagId, entityType, entityId) =>
  unwrap(
    client.post("/customizations/tags/assignments", {
      tag_id: tagId,
      entity_type: entityType,
      entity_id: entityId,
    }),
  );

export const detachTag = (tagId, entityType, entityId) =>
  unwrap(
    client.delete(
      `/customizations/tags/assignments/by-link?tag_id=${tagId}&entity_type=${entityType}&entity_id=${entityId}`,
    ),
  );

export const bulkApplyTags = ({ entityType, entityIds, add = [], remove = [] }) =>
  unwrap(
    client.post("/customizations/tags/apply-bulk", {
      entity_type: entityType,
      entity_ids: entityIds,
      add,
      remove,
    }),
  );

/* Document Types (v1.59) — anagrafica Tipi documento */
export const listDocumentTypes = (opts = {}) => {
  // Accept both includeInactive (nostro naming) e includeArchived
  // (usato da SimpleCrudTab per compatibilità con gli altri CRUD).
  const inactive = opts.includeInactive || opts.includeArchived;
  const q = inactive ? "?include_inactive=true" : "";
  return unwrap(client.get(`/customizations/document-types${q}`));
};
export const createDocumentType = (payload) =>
  unwrap(client.post("/customizations/document-types", payload));
export const updateDocumentType = (id, payload) =>
  unwrap(client.patch(`/customizations/document-types/${id}`, payload));
export const deleteDocumentType = (id) =>
  unwrap(client.delete(`/customizations/document-types/${id}`));
export const reactivateDocumentType = (id) =>
  unwrap(client.post(`/customizations/document-types/${id}/reactivate`));

/* Attachments (v1.59 ck14.2) */
export const uploadAttachment = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return unwrap(
    client.post("/attachments", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  );
};
export const attachmentDownloadUrl = (attachmentId, entityType, entityId) => {
  const base = `${API}/attachments/${attachmentId}`;
  if (entityType && entityId) {
    return `${base}?entity_type=${entityType}&entity_id=${entityId}`;
  }
  return base;
};
export const listMovementAttachments = (entityType, entityId) =>
  unwrap(client.get(`/movements/${entityType}/${entityId}/attachments`));
export const linkAttachment = (entityType, entityId, attachmentId, isPrimary = false) =>
  unwrap(
    client.post(`/movements/${entityType}/${entityId}/attachments`, {
      attachment_id: attachmentId,
      is_primary: isPrimary,
    }),
  );
export const unlinkAttachment = (entityType, entityId, attachmentId) =>
  unwrap(
    client.delete(`/movements/${entityType}/${entityId}/attachments/${attachmentId}`),
  );

/* App Settings (session timeout + Cruscotto defaults) */
export const getAppSettings = () =>
  unwrap(client.get("/customizations/app-settings"));
export const updateAppSettings = (payload) =>
  unwrap(client.put("/customizations/app-settings", payload));
export const getPublicAppSettings = () =>
  unwrap(client.get("/customizations/app-settings/public"));

/* Expenses (master) */
export const listExpenses = (params = {}) =>
  cachedGet("/expenses", params, () =>
    unwrap(client.get("/expenses", { params })),
  );
export const getExpense = (id) => unwrap(client.get(`/expenses/${id}`));
export const createExpense = (payload) => {
  bust("/expenses");
  return unwrap(client.post("/expenses", payload));
};
export const updateExpense = (id, payload) => {
  bust("/expenses");
  return unwrap(client.patch(`/expenses/${id}`, payload));
};
export const stopExpense = (id) => {
  bust("/expenses");
  return unwrap(client.post(`/expenses/${id}/stop`));
};
export const archiveExpense = (id) => {
  bust("/expenses");
  return unwrap(client.post(`/expenses/${id}/archive`));
};
export const unarchiveExpense = (id) => {
  bust("/expenses");
  return unwrap(client.post(`/expenses/${id}/unarchive`));
};
export const reactivateExpense = (id) => {
  bust("/expenses");
  return unwrap(client.post(`/expenses/${id}/reactivate`));
};
export const deleteExpense = (id) => {
  bust("/expenses");
  return unwrap(client.delete(`/expenses/${id}`));
};

/* Installments (Piano rateale) */
export const listInstallments = (expenseId) =>
  unwrap(client.get(`/expenses/${expenseId}/installments`));
export const addInstallment = (expenseId, payload) => {
  bust("/expenses");
  return unwrap(client.post(`/expenses/${expenseId}/installments`, payload));
};
export const updateInstallment = (expenseId, occId, payload) => {
  bust("/expenses");
  return unwrap(client.patch(`/expenses/${expenseId}/installments/${occId}`, payload));
};
export const deleteInstallment = (expenseId, occId) => {
  bust("/expenses");
  return unwrap(client.delete(`/expenses/${expenseId}/installments/${occId}`));
};

/* Occurrences */
export const listOccurrences = (params = {}) =>
  unwrap(client.get("/occurrences", { params }));
export const getOccurrence = (id) => unwrap(client.get(`/occurrences/${id}`));
export const patchOccurrence = (id, payload) => unwrap(client.patch(`/occurrences/${id}`, payload));
export const postponeOccurrence = (id, payload) =>
  unwrap(client.post(`/occurrences/${id}/postpone`, payload));
export const skipOccurrence = (id) => unwrap(client.post(`/occurrences/${id}/skip`));
export const markOccurrenceNotDue = (id) =>
  unwrap(client.post(`/occurrences/${id}/mark_not_due`));
export const restoreOccurrence = (id) =>
  unwrap(client.post(`/occurrences/${id}/restore`));

/* Payments */
export const listPayments = (params = {}) => unwrap(client.get("/payments", { params }));
// v1.60.3: accetta opts.idempotencyKey per protezione doppio-clic/retry
// server-side (header Idempotency-Key). Retro-compatibile: senza opts
// il comportamento è un POST normale.
export const createPayment = (payload, opts = {}) => {
  if (opts.idempotencyKey) {
    const { postFinancialMovement } = require("@/lib/idempotency");
    return postFinancialMovement("/payments", payload, opts).then((r) => r.data);
  }
  return unwrap(client.post("/payments", payload));
};
export const updatePayment = (id, payload) => unwrap(client.patch(`/payments/${id}`, payload));
export const deletePayment = (id) => unwrap(client.delete(`/payments/${id}`));
export const paymentsBreakdown = (params) =>
  unwrap(client.get("/payments/breakdown", { params }));

/* Forecast */
export const monthlyForecast = (params) =>
  unwrap(client.get("/forecast/monthly", { params }));
export const yearlyForecast = (params) =>
  unwrap(client.get("/forecast/yearly", { params }));
export const categoryForecast = (params) =>
  unwrap(client.get("/forecast/by-category", { params }));
export const listAdjustments = (params) =>
  unwrap(client.get("/forecast/adjustments", { params }));
export const createAdjustment = (payload) =>
  unwrap(client.post("/forecast/adjustments", payload));
export const deleteAdjustment = (id) =>
  unwrap(client.delete(`/forecast/adjustments/${id}`));

/* Variance */
export const varianceMonthly = (params) =>
  unwrap(client.get("/variance/monthly", { params }));
export const varianceByCategory = (params) =>
  unwrap(client.get("/variance/by-category", { params }));
export const varianceCumulative = (params) =>
  unwrap(client.get("/variance/cumulative", { params }));
export const varianceReconciliation = (params) =>
  unwrap(client.get("/variance/reconciliation", { params }));
export const occurrenceVariance = (id) =>
  unwrap(client.get(`/variance/occurrence/${id}`));

/* Analytics (advanced internal reporting) */
export const analyticsByVendor = (params) =>
  unwrap(client.get("/analytics/by-vendor", { params }));
export const analyticsVendorTrend = (params) =>
  unwrap(client.get("/analytics/vendor-trend", { params }));
export const analyticsByCategoryDeep = (params) =>
  unwrap(client.get("/analytics/by-category", { params }));
export const analyticsCategoryTrend = (params) =>
  unwrap(client.get("/analytics/category-trend", { params }));
export const analyticsMatrix = (params) =>
  unwrap(client.get("/analytics/matrix", { params }));
export const analyticsRecurringVsOnetime = (params) =>
  unwrap(client.get("/analytics/recurring-vs-onetime", { params }));
export const analyticsAnnualSummary = (params) =>
  unwrap(client.get("/analytics/annual-summary", { params }));

/* Prima Nota — vista consuntiva derivata (Payment + Collection + DailyReceipt orfani) */
export const listPrimaNota = (params = {}) =>
  unwrap(client.get("/prima-nota", { params }));
export const primaNotaSummary = (params = {}) =>
  unwrap(client.get("/prima-nota/summary", { params }));
